import { inject, Injectable } from '@angular/core';
import { RootStore } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Selection } from '../../../../../../core/h3ml/state/fusion-state';
import { TaskWorkspaceStore } from '../../../../../../core/h3ml/state/task-workspace-store';
import { RootPushAndTrackService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/root-push-and-track.service';
import { SelectionService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/selection.service';
import { ObjectSortingService } from '../../../../../../core/shared/misc-services/object-sorting.service';
import {
  objectSequencesFeaturesErrorCause,
  ObjectService,
} from '../../../../../../core/shared/misc-services/object.service';
import { MessagingService } from '../../../../../../cross-team-shared/messaging/messaging.service';
import { keys } from '../../../../../../cross-team-shared/misc-utils/keys';

class ObjectDragAndDropState {
  isDragging = false;
  // undefined is root, null is no target
  targetNestingObjectId?: number | undefined | null = null;
  objectReordering?: {
    previousObjectId?: number;
    nextObjectId?: number;
  };
}

@Injectable()
export class ObjectDragAndDropService extends RootStore<ObjectDragAndDropState> {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private selectionService = inject(SelectionService);
  private objectService = inject(ObjectService);
  private objectSortingService = inject(ObjectSortingService);
  private pushAndTrackService = inject(RootPushAndTrackService);
  private messagingService = inject(MessagingService);

  constructor() {
    super(new ObjectDragAndDropState());
  }

  startObjectDrag(draggedObjectId: number) {
    // fix me if you change this code

    if (!this.taskWorkspaceStore.state().objectSelection[draggedObjectId]) {
      this.selectionService.setSelection([draggedObjectId], []);
    }
    this('isDragging').set(true);
  }

  dropSelectedObjects() {
    try {
      const { targetNestingObjectId, objectReordering } = this.state();
      if (targetNestingObjectId !== null) {
        this.nestSelectedObjects(targetNestingObjectId);
      } else if (isDefined(objectReordering)) {
        this.reorderSelectedObjects(
          objectReordering.previousObjectId,
          objectReordering.nextObjectId,
        );
      }
    } finally {
      this.set(new ObjectDragAndDropState());
    }
  }

  canBeDroppedOn$(targetNestingObjectId: number) {
    return combineLatest([
      this.taskWorkspaceStore('objectSelection').$,
      this('isDragging').$,
    ]).pipe(
      map(
        ([objectSelection, isDragging]) =>
          isDragging && !objectSelection[targetNestingObjectId],
      ),
    );
  }

  canDrop$() {
    return this.$.pipe(
      map(
        (state) =>
          state.isDragging &&
          isDefined(state.objectReordering ?? state.targetNestingObjectId),
      ),
    );
  }

  isObjectDragged$(objectId: number) {
    return combineLatest([
      this.taskWorkspaceStore('objectSelection').$,
      this('isDragging').$,
    ]).pipe(
      map(
        ([objectSelection, isDragging]) =>
          isDragging && objectSelection[objectId],
      ),
    );
  }

  setReorderingUsingPreviousObjectId(previousObjectId: number) {
    const { objectSelection } = this.taskWorkspaceStore.state();
    const sortedObjectIds = this.objectSortingService
      .getSortedObjects(this.taskWorkspaceStore.state().scopedObjectId)
      // @ts-ignore -- fix me
      .map((object) => object.index);
    const order = sortedObjectIds.filter(
      // fix me if you change this code

      (objectId) => !objectSelection[objectId],
    );
    this('objectReordering').set({
      previousObjectId,
      nextObjectId: order[order.indexOf(previousObjectId) + 1],
    });
  }

  setReorderingUsingNextObjectId(nextObjectId: number) {
    const { objectSelection } = this.taskWorkspaceStore.state();
    const sortedObjectIds = this.objectSortingService
      .getSortedObjects(this.taskWorkspaceStore.state().scopedObjectId)
      // @ts-ignore -- fix me
      .map((object) => object.index);
    const order = sortedObjectIds.filter(
      // fix me if you change this code

      (objectId) => !objectSelection[objectId],
    );
    this('objectReordering').set({
      previousObjectId: order[order.indexOf(nextObjectId) - 1],
      nextObjectId,
    });
  }

  willReorderBefore$(objectId: number) {
    return this('objectReordering').$.pipe(
      map((reordering) => reordering?.nextObjectId === objectId),
    );
  }

  willReorderAfter$(objectId: number) {
    return this('objectReordering').$.pipe(
      map((reordering) => reordering?.previousObjectId === objectId),
    );
  }

  private reorderSelectedObjects(
    previousObjectId: number | undefined,
    nextObjectId: number | undefined,
  ) {
    this.objectSortingService.updateSortIndexes(previousObjectId, nextObjectId);
    const { objectSelection } = this.taskWorkspaceStore.state();
    this.pushAndTrack(objectSelection, 'reorder');
  }

  private nestSelectedObjects(targetNestingObjectId: number | undefined) {
    this.taskWorkspaceStore.batch(() => {
      const { objectSelection } = this.taskWorkspaceStore.state();

      try {
        this.objectService.nestSelectedObjects(targetNestingObjectId);
      } catch (error) {
        if ((error as Error).cause === objectSequencesFeaturesErrorCause) {
          this.messagingService.error((error as Error).message);
        }
      }

      const selectedObjectIds = keys(objectSelection);
      const sortedObjectsInNewScope = this.objectSortingService
        .getSortedObjects(targetNestingObjectId)
        .filter((object) => !selectedObjectIds.includes(object.index));
      if (sortedObjectsInNewScope.length) {
        this.objectSortingService.updateSortIndexes(
          undefined,
          // @ts-ignore -- fix me
          sortedObjectsInNewScope[0].index,
          objectSelection,
        );
      }
      const method = isDefined(targetNestingObjectId) ? 'nest' : 'unnest';
      this.removeChildObjectIds();
      this.pushAndTrack(objectSelection, method);
    });
  }

  private removeChildObjectIds() {
    const { objectSelection, scopedObjectId, objects } =
      this.taskWorkspaceStore.state();

    if (isUndefined(scopedObjectId) || !objects[scopedObjectId]) {
      return;
    }

    this.taskWorkspaceStore('objects')(scopedObjectId)(
      // @ts-ignore -- fix me
      'childObjectIds',
    ).setUsing((childObjectIds) =>
      // @ts-ignore -- fix me
      childObjectIds.filter((childObjectId) => !objectSelection[childObjectId]),
    );
  }

  private pushAndTrack(objectSelection: Selection, method: string) {
    this.pushAndTrackService.pushAndTrack({
      id: 'reorder_object',
      bucket: 'adjustment',
      source: 'drag_and_drop',
      objectIds: keys(objectSelection),
      method,
    });
  }
}
